<template>
  <component
    :is="contentComponent"
    id="container"
    class="sp-navigation"
    :title="collapsableMenuTitle"
    :class="classModifiers"
    :style="styleVariables"
    :expanded="forceCollapse"
    @update-expanded="handleUpdateExpanded"
  >
    <div class="sp-navigation__container" part="container">
      <slot part="default-slot" class="default-slot" @click="handleCollapse" />

      <div v-if="stuckToTop" part="stuck-to-top" @click="handleCollapse">
        <slot name="stuck-to-top" />
      </div>
    </div>
  </component>
</template>

<script setup>
/**
 * The SpNavigation component is used to display a navigation bar.
 *
 * @displayName Navigation
 * @group Custom Elements
 * @component sp-navigation
 */

import { computed, ref, nextTick, useHost, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { toBoolean } from "#ce/utils/props";
import { props as navProps } from "./props.js";

const { isGtSmScreen } = useBreakpoints();
const host = useHost();

defineOptions({
  inheritAttrs: false,
});

const props = defineProps(navProps);

const classModifiers = computed(() => ({
  "--is-stuck-to-top": toBoolean(props.stuckToTop),
}));

const isAlwaysCollapsibleOrSmall = computed(() => toBoolean(props.alwaysCollapsible) || !isGtSmScreen.value);

const styleVariables = computed(() => ({
  "--direction": isAlwaysCollapsibleOrSmall.value ? "column" : "row",
}));

const contentComponent = computed(() =>
  toBoolean(props.collapsable) && isAlwaysCollapsibleOrSmall.value ? "sp-expandable-area" : "nav",
);

const isExpanded = ref(false);
const forceCollapse = ref(false);

// Mirrors the collapsable menu's open state to the host as `menuExpanded`, so an enclosing
// sp-toolbar can exclude the open panel from its anchor scroll-padding measurement — the
// counterpart of the `stuckToTop` prop the toolbar writes onto this element.
watch(isExpanded, (value) => {
  host.menuExpanded = value;
});

// The expandable area is destroyed on the switch to a plain nav, so its expansion state
// does not survive the switch.
watch(contentComponent, () => {
  isExpanded.value = false;
});

function handleUpdateExpanded(event) {
  isExpanded.value = event.detail[0];
}

function handleCollapse() {
  if (toBoolean(props.collapseOnContentClick) && isAlwaysCollapsibleOrSmall.value) {
    if (isExpanded.value) {
      forceCollapse.value = true;
      nextTick(() => {
        forceCollapse.value = false;
      });
    }
  }
}
</script>

<style>
:host {
  display: block;
}
</style>

<style scoped lang="scss">
.sp-navigation {
  --direction: row;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2rem;
  flex-direction: var(--direction);
  width: 100%;
}

.default-slot {
  display: var(--sp-comp-navigation-default-slot-display, flex);
  width: var(--sp-comp-navigation-default-slot-width, 100%);
  align-items: var(--sp-comp-navigation-default-slot-align-items, center);
  justify-content: var(--sp-comp-navigation-default-slot-justify-content, flex-start);
  gap: 2rem;
}
.sp-navigation__container {
  width: 100%;
}
</style>
